/**
 * Admin Cloud Scans Page
 * Dashboard for administrators to view all cloud scans
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import MainLayout from '../../components/layout/MainLayout';
import cloudScanApi, { ScanResult, SecurityFinding, CostRecommendation, Severity } from '../../services/api/cloud-scan.api';
import toast from 'react-hot-toast';

interface CloudScan {
    id: string;
    provider: 'aws' | 'azure' | 'gcp';
    scanType: 'full' | 'security' | 'cost';
    status: 'pending' | 'running' | 'completed' | 'failed';
    isPaid: boolean;
    paymentAmount?: number;
    paidAt?: string;
    totalSecurityFindings: number;
    totalCostRecommendations: number;
    criticalIssues: number;
    estimatedMonthlySavings: number;
    durationMs: number;
    createdAt: string;
    completedAt?: string;
    user: {
        id: string;
        email: string;
        firstName: string;
        lastName: string;
    } | null;
    tenant: {
        id: string;
        name: string;
    } | null;
}

interface AdminStats {
    totalScans: number;
    completedScans: number;
    failedScans: number;
    paidScans: number;
    totalRevenue: number;
    conversionRate: string;
    scansByProvider: {
        aws: number;
        azure: number;
        gcp: number;
    };
}

const ProviderIcon = ({ provider }: { provider: string }) => {
    switch (provider) {
        case 'aws':
            return <span className="text-orange-500 font-bold text-sm">AWS</span>;
        case 'azure':
            return <span className="text-blue-500 font-bold text-sm">Azure</span>;
        case 'gcp':
            return <span className="text-red-500 font-bold text-sm">GCP</span>;
        default:
            return <span className="text-gray-500">{provider}</span>;
    }
};

const StatusBadge = ({ status }: { status: string }) => {
    const colors: Record<string, string> = {
        pending: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
        running: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
        completed: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
        failed: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
    };

    return (
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${colors[status] || 'bg-gray-100 text-gray-800'}`}>
            {status}
        </span>
    );
};

const getSeverityColor = (severity: Severity) => {
    switch (severity) {
        case 'critical':
            return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400 border-red-200 dark:border-red-800';
        case 'high':
            return 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400 border-orange-200 dark:border-orange-800';
        case 'medium':
            return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400 border-yellow-200 dark:border-yellow-800';
        case 'low':
            return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400 border-blue-200 dark:border-blue-800';
        case 'info':
            return 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400 border-gray-200 dark:border-gray-800';
    }
};

export default function AdminCloudScansPage() {
    const { t } = useTranslation(['cloudScan', 'common']);
    const [scans, setScans] = useState<CloudScan[]>([]);
    const [stats, setStats] = useState<AdminStats | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Filters
    const [providerFilter, setProviderFilter] = useState<string>('');
    const [statusFilter, setStatusFilter] = useState<string>('');
    const [paidFilter, setPaidFilter] = useState<string>('');
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);
    const limit = 20;

    // Scan details modal
    const [selectedScan, setSelectedScan] = useState<CloudScan | null>(null);
    const [scanDetails, setScanDetails] = useState<ScanResult | null>(null);
    const [loadingDetails, setLoadingDetails] = useState(false);
    const [activeTab, setActiveTab] = useState<'security' | 'cost'>('security');
    const [expandedFinding, setExpandedFinding] = useState<string | null>(null);
    const [severityFilter, setSeverityFilter] = useState<Severity | 'all'>('all');
    const [typeFilter, setTypeFilter] = useState<string>('all');

    useEffect(() => {
        loadData();
        loadStats();
    }, []);

    useEffect(() => {
        loadData();
    }, [providerFilter, statusFilter, paidFilter, page]);

    const loadData = async () => {
        try {
            setLoading(true);
            const result = await cloudScanApi.getAdminScans({
                limit,
                offset: (page - 1) * limit,
                provider: providerFilter || undefined,
                status: statusFilter || undefined,
                isPaid: paidFilter || undefined,
            });

            if (result.success) {
                setScans(result.data.scans);
                setTotal(result.data.total);
            } else {
                setError('Failed to load scans');
            }
        } catch (err: any) {
            console.error('Failed to load scans:', err);
            setError(err.message || 'Failed to load scans');
        } finally {
            setLoading(false);
        }
    };

    const loadStats = async () => {
        try {
            const result = await cloudScanApi.getAdminStats();

            if (result.success) {
                setStats(result.data);
            }
        } catch (err) {
            console.error('Failed to load stats:', err);
        }
    };

    const formatDate = (date: string) => {
        return new Date(date).toLocaleString();
    };

    const formatDuration = (ms: number) => {
        if (ms < 1000) return `${ms}ms`;
        if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
        return `${(ms / 60000).toFixed(1)}m`;
    };

    const formatCurrency = (amount: number) => {
        return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
    };

    const totalPages = Math.ceil(total / limit);

    const handleViewDetails = async (scan: CloudScan) => {
        setSelectedScan(scan);
        setLoadingDetails(true);
        setScanDetails(null);
        setActiveTab('security');
        setExpandedFinding(null);
        setSeverityFilter('all');
        setTypeFilter('all');

        try {
            const response = await cloudScanApi.getAdminScanDetails(scan.id);
            if (response.success) {
                setScanDetails(response.data);
            } else {
                setError('Failed to load scan details');
            }
        } catch (err: any) {
            setError(err.message || 'Failed to load scan details');
        } finally {
            setLoadingDetails(false);
        }
    };

    const handleExportExcel = async (scanId: string) => {
        try {
            const response = await fetch(`${import.meta.env.VITE_API_URL}/v1/cloud-scan/scans/${scanId}/export/excel`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`,
                },
            });

            if (!response.ok) {
                // Try to parse error as JSON, fallback to text
                let errorMessage = 'Failed to export Excel';
                try {
                    const contentType = response.headers.get('content-type');
                    if (contentType && contentType.includes('application/json')) {
                        const error = await response.json();
                        errorMessage = error.error || error.message || errorMessage;
                    } else {
                        const text = await response.text();
                        errorMessage = text || `HTTP ${response.status}: ${response.statusText}`;
                    }
                } catch (parseError) {
                    errorMessage = `HTTP ${response.status}: ${response.statusText}`;
                }
                throw new Error(errorMessage);
            }

            // Get filename from Content-Disposition header or create default
            const contentDisposition = response.headers.get('Content-Disposition');
            const filenameMatch = contentDisposition?.match(/filename="(.+)"/);
            const filename = filenameMatch ? filenameMatch[1] : `cloud-scan-${scanId}-${Date.now()}.xlsx`;

            // Create blob and download
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
        } catch (error: any) {
            console.error('Failed to export Excel:', error);
            toast.error(error.message || t('cloudScan:errors.exportFailed', 'Failed to export report'), {
                duration: 5000,
                icon: '❌',
            });
        }
    };

    const handleExportPDF = async (scanId: string) => {
        try {
            const response = await fetch(`${import.meta.env.VITE_API_URL}/v1/cloud-scan/scans/${scanId}/export/pdf`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`,
                },
            });

            if (!response.ok) {
                // Try to parse error as JSON, fallback to text
                let errorMessage = 'Failed to export PDF';
                try {
                    const contentType = response.headers.get('content-type');
                    if (contentType && contentType.includes('application/json')) {
                        const error = await response.json();
                        errorMessage = error.error || error.message || errorMessage;
                    } else {
                        const text = await response.text();
                        errorMessage = text || `HTTP ${response.status}: ${response.statusText}`;
                    }
                } catch (parseError) {
                    errorMessage = `HTTP ${response.status}: ${response.statusText}`;
                }
                throw new Error(errorMessage);
            }

            // Get filename from Content-Disposition header or create default
            const contentDisposition = response.headers.get('Content-Disposition');
            const filenameMatch = contentDisposition?.match(/filename="(.+)"/);
            const filename = filenameMatch ? filenameMatch[1] : `cloud-scan-${scanId}-${Date.now()}.pdf`;

            // Create blob and download
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
        } catch (error: any) {
            console.error('Failed to export PDF:', error);
            toast.error(error.message || t('cloudScan:errors.exportFailed', 'Failed to export report'), {
                duration: 5000,
                icon: '❌',
            });
        }
    };

    // Helper functions for filtering and grouping
    const getFilteredFindings = () => {
        if (!scanDetails?.security?.findings) return [];
        let filtered = scanDetails.security.findings;

        if (severityFilter !== 'all') {
            filtered = filtered.filter((f: any) => f.severity === severityFilter);
        }

        if (typeFilter !== 'all') {
            filtered = filtered.filter((f: any) => f.type === typeFilter);
        }

        return filtered;
    };

    const getFilteredRecommendations = () => {
        if (!scanDetails?.cost?.recommendations) return [];
        let filtered = scanDetails.cost.recommendations;

        if (typeFilter !== 'all') {
            filtered = filtered.filter((r: any) => r.type === typeFilter);
        }

        return filtered;
    };

    const getFindingTypeCounts = () => {
        if (!scanDetails?.security?.findings) return {};
        const counts: Record<string, number> = {};
        scanDetails.security.findings.forEach((f: any) => {
            const type = f.type || 'other';
            counts[type] = (counts[type] || 0) + 1;
        });
        return counts;
    };

    const getRecommendationTypeCounts = () => {
        if (!scanDetails?.cost?.recommendations) return {};
        const counts: Record<string, number> = {};
        scanDetails.cost.recommendations.forEach((r: any) => {
            const type = r.type || 'other';
            counts[type] = (counts[type] || 0) + 1;
        });
        return counts;
    };

    const getTypeLabel = (type: string) => {
        const labels: Record<string, string> = {
            open_port: 'Open Ports',
            public_acl: 'Public S3 Buckets',
            key_without_mfa: 'IAM Keys Without MFA',
            no_mfa: 'Users Without MFA',
            console_and_key: 'Console + Key Access',
            unused_key: 'Unused Access Keys',
            old_access_key: 'Old Access Keys',
            policy_wildcard_resource: 'Wildcard Policies',
            unused_role: 'Unused IAM Roles',
            idle_instance: 'Idle EC2 Instances',
            orphan_volume: 'Orphan EBS Volumes',
            old_snapshot: 'Old Snapshots',
            s3_bucket_review: 'S3 Buckets Review',
            underutilized_rds: 'Underutilized RDS',
            idle_load_balancer: 'Idle Load Balancers',
        };
        return labels[type] || type.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
    };

    return (
        <MainLayout>
            <div className="p-6">
                {/* Header */}
                <div className="mb-8">
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                        Cloud Scans Administration
                    </h1>
                    <p className="text-gray-600 dark:text-gray-400 mt-1">
                        Monitor all cloud infrastructure scans and revenue
                    </p>
                </div>

                {/* Stats Cards */}
                {stats && (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                        <div className="bg-white dark:bg-gray-800 rounded-xl p-5 border border-gray-200 dark:border-gray-700">
                            <div className="text-sm text-gray-500 dark:text-gray-400">Total Scans</div>
                            <div className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                                {stats.totalScans}
                            </div>
                            <div className="text-xs text-gray-400 mt-2">
                                {stats.completedScans} completed • {stats.failedScans} failed
                            </div>
                        </div>

                        <div className="bg-white dark:bg-gray-800 rounded-xl p-5 border border-gray-200 dark:border-gray-700">
                            <div className="text-sm text-gray-500 dark:text-gray-400">Paid Scans</div>
                            <div className="text-2xl font-bold text-green-600 dark:text-green-400 mt-1">
                                {stats.paidScans}
                            </div>
                            <div className="text-xs text-gray-400 mt-2">
                                {stats.conversionRate}% conversion rate
                            </div>
                        </div>

                        <div className="bg-white dark:bg-gray-800 rounded-xl p-5 border border-gray-200 dark:border-gray-700">
                            <div className="text-sm text-gray-500 dark:text-gray-400">Total Revenue</div>
                            <div className="text-2xl font-bold text-indigo-600 dark:text-indigo-400 mt-1">
                                {formatCurrency(stats.totalRevenue)}
                            </div>
                            <div className="text-xs text-gray-400 mt-2">
                                From full report purchases
                            </div>
                        </div>

                        <div className="bg-white dark:bg-gray-800 rounded-xl p-5 border border-gray-200 dark:border-gray-700">
                            <div className="text-sm text-gray-500 dark:text-gray-400">Scans by Provider</div>
                            <div className="flex items-center gap-4 mt-2">
                                <div className="text-center">
                                    <div className="text-lg font-bold text-orange-500">{stats.scansByProvider.aws}</div>
                                    <div className="text-xs text-gray-400">AWS</div>
                                </div>
                                <div className="text-center">
                                    <div className="text-lg font-bold text-blue-500">{stats.scansByProvider.azure}</div>
                                    <div className="text-xs text-gray-400">Azure</div>
                                </div>
                                <div className="text-center">
                                    <div className="text-lg font-bold text-red-500">{stats.scansByProvider.gcp}</div>
                                    <div className="text-xs text-gray-400">GCP</div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Filters */}
                <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 mb-6">
                    <div className="flex flex-wrap gap-4">
                        <select
                            value={providerFilter}
                            onChange={(e) => setProviderFilter(e.target.value)}
                            className="px-3 py-2 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg text-sm"
                        >
                            <option value="">All Providers</option>
                            <option value="aws">AWS</option>
                            <option value="azure">Azure</option>
                            <option value="gcp">GCP</option>
                        </select>

                        <select
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                            className="px-3 py-2 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg text-sm"
                        >
                            <option value="">All Statuses</option>
                            <option value="pending">Pending</option>
                            <option value="running">Running</option>
                            <option value="completed">Completed</option>
                            <option value="failed">Failed</option>
                        </select>

                        <select
                            value={paidFilter}
                            onChange={(e) => setPaidFilter(e.target.value)}
                            className="px-3 py-2 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg text-sm"
                        >
                            <option value="">All Payment Status</option>
                            <option value="true">Paid</option>
                            <option value="false">Free</option>
                        </select>

                        <button
                            onClick={() => {
                                setProviderFilter('');
                                setStatusFilter('');
                                setPaidFilter('');
                                setPage(1);
                            }}
                            className="px-3 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                        >
                            Clear Filters
                        </button>
                    </div>
                </div>

                {/* Scans Table */}
                <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
                    {loading ? (
                        <div className="p-8 text-center text-gray-500">Loading...</div>
                    ) : error ? (
                        <div className="p-8 text-center text-red-500">{error}</div>
                    ) : scans.length === 0 ? (
                        <div className="p-8 text-center text-gray-500">No scans found</div>
                    ) : (
                        <>
                            <div className="overflow-x-auto">
                                <table className="w-full">
                                    <thead className="bg-gray-50 dark:bg-gray-900/50">
                                        <tr>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                                                Provider
                                            </th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                                                User
                                            </th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                                                Status
                                            </th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                                                Findings
                                            </th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                                                Paid
                                            </th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                                                Duration
                                            </th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                                                Date
                                            </th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                                                Actions
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                                        {scans.map((scan) => (
                                            <tr key={scan.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                                                <td className="px-4 py-3">
                                                    <div className="flex items-center gap-2">
                                                        <ProviderIcon provider={scan.provider} />
                                                        <span className="text-xs text-gray-500 capitalize">
                                                            {scan.scanType}
                                                        </span>
                                                    </div>
                                                </td>
                                                <td className="px-4 py-3">
                                                    {scan.user ? (
                                                        <div>
                                                            <div className="text-sm text-gray-900 dark:text-white">
                                                                {scan.user.firstName} {scan.user.lastName}
                                                            </div>
                                                            <div className="text-xs text-gray-500">{scan.user.email}</div>
                                                        </div>
                                                    ) : (
                                                        <span className="text-gray-400">-</span>
                                                    )}
                                                </td>
                                                <td className="px-4 py-3">
                                                    <StatusBadge status={scan.status} />
                                                </td>
                                                <td className="px-4 py-3">
                                                    <div className="text-sm">
                                                        <span className="text-gray-900 dark:text-white">
                                                            {scan.totalSecurityFindings}
                                                        </span>
                                                        <span className="text-gray-500"> security</span>
                                                        {scan.criticalIssues > 0 && (
                                                            <span className="ml-1 text-red-500">
                                                                ({scan.criticalIssues} critical)
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div className="text-xs text-gray-500">
                                                        {scan.totalCostRecommendations} cost •{' '}
                                                        {formatCurrency(scan.estimatedMonthlySavings)}/mo savings
                                                    </div>
                                                </td>
                                                <td className="px-4 py-3">
                                                    {scan.isPaid ? (
                                                        <div>
                                                            <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
                                                                Paid
                                                            </span>
                                                            {scan.paymentAmount && (
                                                                <div className="text-xs text-gray-500 mt-1">
                                                                    {formatCurrency(scan.paymentAmount)}
                                                                </div>
                                                            )}
                                                        </div>
                                                    ) : (
                                                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400">
                                                            Free
                                                        </span>
                                                    )}
                                                </td>
                                                <td className="px-4 py-3 text-sm text-gray-500">
                                                    {formatDuration(scan.durationMs)}
                                                </td>
                                                <td className="px-4 py-3 text-sm text-gray-500">
                                                    {formatDate(scan.createdAt)}
                                                </td>
                                                <td className="px-4 py-3">
                                                    <div className="flex gap-2">
                                                        <button
                                                            onClick={() => handleViewDetails(scan)}
                                                            className="px-3 py-1 text-xs font-medium text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 rounded-lg transition-colors"
                                                            title="View Details"
                                                        >
                                                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                                            </svg>
                                                        </button>
                                                        {scan.status === 'completed' && scan.isPaid && (
                                                            <>
                                                                <button
                                                                    onClick={() => handleExportExcel(scan.id)}
                                                                    className="px-3 py-1 text-xs font-medium text-green-600 dark:text-green-400 hover:text-green-700 dark:hover:text-green-300 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg transition-colors"
                                                                    title="Export to Excel"
                                                                >
                                                                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                                                    </svg>
                                                                </button>
                                                                <button
                                                                    onClick={() => handleExportPDF(scan.id)}
                                                                    className="px-3 py-1 text-xs font-medium text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                                                                    title="Export to PDF"
                                                                >
                                                                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                                                                    </svg>
                                                                </button>
                                                            </>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {/* Pagination */}
                            {totalPages > 1 && (
                                <div className="px-4 py-3 bg-gray-50 dark:bg-gray-900/50 flex items-center justify-between">
                                    <div className="text-sm text-gray-500">
                                        Showing {(page - 1) * limit + 1} to {Math.min(page * limit, total)} of {total}
                                    </div>
                                    <div className="flex gap-2">
                                        <button
                                            onClick={() => setPage(page - 1)}
                                            disabled={page === 1}
                                            className="px-3 py-1 text-sm border rounded-lg disabled:opacity-50"
                                        >
                                            Previous
                                        </button>
                                        <button
                                            onClick={() => setPage(page + 1)}
                                            disabled={page === totalPages}
                                            className="px-3 py-1 text-sm border rounded-lg disabled:opacity-50"
                                        >
                                            Next
                                        </button>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>

                {/* Scan Details Modal */}
                {selectedScan && (
                    <div className="fixed inset-0 z-50 overflow-y-auto">
                        <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
                            {/* Background overlay */}
                            <div
                                className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75 dark:bg-gray-900 dark:bg-opacity-75"
                                onClick={() => {
                                    setSelectedScan(null);
                                    setScanDetails(null);
                                }}
                            ></div>

                            {/* Modal panel */}
                            <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-xl text-left overflow-hidden shadow-2xl transform transition-all sm:my-8 sm:align-middle sm:max-w-7xl sm:w-full">
                                {/* Header */}
                                <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-5">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-4">
                                            <div className="p-2 bg-white/20 rounded-lg backdrop-blur-sm">
                                                <ProviderIcon provider={selectedScan.provider} />
                                            </div>
                                            <div>
                                                <h3 className="text-xl font-bold text-white">
                                                    Scan Details - {selectedScan.provider.toUpperCase()}
                                                </h3>
                                                <p className="text-sm text-indigo-100 mt-1">
                                                    {selectedScan.user
                                                        ? `${selectedScan.user.firstName} ${selectedScan.user.lastName} • ${selectedScan.user.email}`
                                                        : 'No user'}
                                                    {' • '}
                                                    {formatDate(selectedScan.createdAt)}
                                                    {scanDetails && ` • ${formatDuration(scanDetails.duration)}`}
                                                </p>
                                            </div>
                                        </div>
                                        <button
                                            onClick={() => {
                                                setSelectedScan(null);
                                                setScanDetails(null);
                                            }}
                                            className="text-white hover:bg-white/20 p-2 rounded-lg transition-colors"
                                        >
                                            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                            </svg>
                                        </button>
                                    </div>
                                </div>

                                <div className="bg-gray-50 dark:bg-gray-900 px-6 py-6 max-h-[calc(100vh-200px)] overflow-y-auto">
                                    {loadingDetails ? (
                                        <div className="py-12 text-center">
                                            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                                            <p className="mt-4 text-gray-500">Loading scan details...</p>
                                        </div>
                                    ) : scanDetails ? (
                                        <div className="space-y-6">
                                            {/* Summary Cards */}
                                            {scanDetails.report && (
                                                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                                                    <div className="bg-white dark:bg-gray-800 rounded-xl p-5 border border-gray-200 dark:border-gray-700 shadow-sm hover:shadow-md transition-shadow">
                                                        <div className="flex items-center gap-3 mb-2">
                                                            <div className="p-2 bg-red-100 dark:bg-red-900/30 rounded-lg">
                                                                <svg className="w-5 h-5 text-red-600 dark:text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                                                </svg>
                                                            </div>
                                                            <div className="text-sm font-medium text-gray-500 dark:text-gray-400">Security Issues</div>
                                                        </div>
                                                        <div className="text-3xl font-bold text-gray-900 dark:text-white">
                                                            {scanDetails.report.summary.totalSecurityFindings}
                                                        </div>
                                                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                                                            {scanDetails.report.severityBreakdown?.critical || 0} critical • {scanDetails.report.severityBreakdown?.high || 0} high
                                                        </div>
                                                    </div>
                                                    <div className="bg-white dark:bg-gray-800 rounded-xl p-5 border border-gray-200 dark:border-gray-700 shadow-sm hover:shadow-md transition-shadow">
                                                        <div className="flex items-center gap-3 mb-2">
                                                            <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                                                                <svg className="w-5 h-5 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                                                </svg>
                                                            </div>
                                                            <div className="text-sm font-medium text-gray-500 dark:text-gray-400">Cost Optimizations</div>
                                                        </div>
                                                        <div className="text-3xl font-bold text-gray-900 dark:text-white">
                                                            {scanDetails.report.summary.totalCostRecommendations}
                                                        </div>
                                                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                                                            Potential savings available
                                                        </div>
                                                    </div>
                                                    <div className="bg-white dark:bg-gray-800 rounded-xl p-5 border border-gray-200 dark:border-gray-700 shadow-sm hover:shadow-md transition-shadow">
                                                        <div className="flex items-center gap-3 mb-2">
                                                            <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                                                                <svg className="w-5 h-5 text-green-600 dark:text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                                </svg>
                                                            </div>
                                                            <div className="text-sm font-medium text-gray-500 dark:text-gray-400">Potential Savings</div>
                                                        </div>
                                                        <div className="text-3xl font-bold text-green-600 dark:text-green-400">
                                                            {formatCurrency(
                                                                scanDetails.report.summary.estimatedTotalMonthlySavings ||
                                                                (scanDetails.report.summary as any).estimatedMonthlySavings ||
                                                                (scanDetails.cost?.recommendations || []).reduce((sum: number, rec: any) => sum + (rec.estimatedMonthlySavings || 0), 0) ||
                                                                0
                                                            )}
                                                        </div>
                                                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                                                            Per month • {formatCurrency(
                                                                ((scanDetails.report.summary.estimatedTotalMonthlySavings ||
                                                                (scanDetails.report.summary as any).estimatedMonthlySavings ||
                                                                (scanDetails.cost?.recommendations || []).reduce((sum: number, rec: any) => sum + (rec.estimatedMonthlySavings || 0), 0) ||
                                                                0) * 12)
                                                            )}/year
                                                        </div>
                                                    </div>
                                                    <div className="bg-white dark:bg-gray-800 rounded-xl p-5 border border-gray-200 dark:border-gray-700 shadow-sm hover:shadow-md transition-shadow">
                                                        <div className="flex items-center gap-3 mb-2">
                                                            <div className="p-2 bg-orange-100 dark:bg-orange-900/30 rounded-lg">
                                                                <svg className="w-5 h-5 text-orange-600 dark:text-orange-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                                                                </svg>
                                                            </div>
                                                            <div className="text-sm font-medium text-gray-500 dark:text-gray-400">Critical Issues</div>
                                                        </div>
                                                        <div className="text-3xl font-bold text-red-600 dark:text-red-400">
                                                            {scanDetails.report.severityBreakdown?.critical || 0}
                                                        </div>
                                                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                                                            Requires immediate attention
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            {/* Tabs */}
                                            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-1">
                                                <div className="flex gap-2">
                                                    <button
                                                        onClick={() => {
                                                            setActiveTab('security');
                                                            setSeverityFilter('all');
                                                            setTypeFilter('all');
                                                        }}
                                                        className={`flex-1 px-4 py-3 font-medium rounded-lg transition-all ${
                                                            activeTab === 'security'
                                                                ? 'bg-indigo-500 text-white shadow-md'
                                                                : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                                                        }`}
                                                    >
                                                        <div className="flex items-center justify-center gap-2">
                                                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                                                            </svg>
                                                            <span>Security Findings</span>
                                                            <span className={`px-2 py-0.5 rounded-full text-xs ${
                                                                activeTab === 'security' ? 'bg-white/20 text-white' : 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                                                            }`}>
                                                                {scanDetails.security?.findings.length || 0}
                                                            </span>
                                                        </div>
                                                    </button>
                                                    <button
                                                        onClick={() => {
                                                            setActiveTab('cost');
                                                            setSeverityFilter('all');
                                                            setTypeFilter('all');
                                                        }}
                                                        className={`flex-1 px-4 py-3 font-medium rounded-lg transition-all ${
                                                            activeTab === 'cost'
                                                                ? 'bg-indigo-500 text-white shadow-md'
                                                                : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                                                        }`}
                                                    >
                                                        <div className="flex items-center justify-center gap-2">
                                                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                            </svg>
                                                            <span>Cost Recommendations</span>
                                                            <span className={`px-2 py-0.5 rounded-full text-xs ${
                                                                activeTab === 'cost' ? 'bg-white/20 text-white' : 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                                                            }`}>
                                                                {scanDetails.cost?.recommendations.length || 0}
                                                            </span>
                                                        </div>
                                                    </button>
                                                </div>
                                            </div>

                                            {/* Filters */}
                                            {activeTab === 'security' && scanDetails.security?.findings.length > 0 && (
                                                <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
                                                    <div className="flex flex-wrap gap-4 items-center">
                                                        <div className="flex items-center gap-2">
                                                            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Severity:</span>
                                                            <select
                                                                value={severityFilter}
                                                                onChange={(e) => setSeverityFilter(e.target.value as Severity | 'all')}
                                                                className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                                                            >
                                                                <option value="all">All Severities</option>
                                                                <option value="critical">Critical</option>
                                                                <option value="high">High</option>
                                                                <option value="medium">Medium</option>
                                                                <option value="low">Low</option>
                                                                <option value="info">Info</option>
                                                            </select>
                                                        </div>
                                                        <div className="flex items-center gap-2">
                                                            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Type:</span>
                                                            <select
                                                                value={typeFilter}
                                                                onChange={(e) => setTypeFilter(e.target.value)}
                                                                className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                                                            >
                                                                <option value="all">All Types</option>
                                                                {Object.entries(getFindingTypeCounts()).map(([type, count]) => (
                                                                    <option key={type} value={type}>
                                                                        {getTypeLabel(type)} ({count})
                                                                    </option>
                                                                ))}
                                                            </select>
                                                        </div>
                                                        <div className="ml-auto text-sm text-gray-500 dark:text-gray-400">
                                                            Showing {getFilteredFindings().length} of {scanDetails.security.findings.length} findings
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            {activeTab === 'cost' && scanDetails.cost?.recommendations.length > 0 && (
                                                <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
                                                    <div className="flex flex-wrap gap-4 items-center">
                                                        <div className="flex items-center gap-2">
                                                            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Type:</span>
                                                            <select
                                                                value={typeFilter}
                                                                onChange={(e) => setTypeFilter(e.target.value)}
                                                                className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                                                            >
                                                                <option value="all">All Types</option>
                                                                {Object.entries(getRecommendationTypeCounts()).map(([type, count]) => (
                                                                    <option key={type} value={type}>
                                                                        {getTypeLabel(type)} ({count})
                                                                    </option>
                                                                ))}
                                                            </select>
                                                        </div>
                                                        <div className="ml-auto text-sm text-gray-500 dark:text-gray-400">
                                                            Showing {getFilteredRecommendations().length} of {scanDetails.cost.recommendations.length} recommendations
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            {/* Security Findings */}
                                            {activeTab === 'security' && (
                                                <div className="space-y-3">
                                                    {getFilteredFindings().length === 0 ? (
                                                        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-12 text-center">
                                                            <svg className="w-12 h-12 mx-auto text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                                            </svg>
                                                            <p className="text-gray-500 dark:text-gray-400">No findings match the selected filters</p>
                                                        </div>
                                                    ) : (
                                                        getFilteredFindings().map((finding: any, index: number) => {
                                                            const isExpanded = expandedFinding === `finding-${index}`;
                                                            return (
                                                                <div
                                                                    key={`finding-${index}`}
                                                                    className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden hover:shadow-md transition-shadow"
                                                                >
                                                                    <button
                                                                        onClick={() => setExpandedFinding(isExpanded ? null : `finding-${index}`)}
                                                                        className="w-full p-5 text-left"
                                                                    >
                                                                        <div className="flex items-start justify-between gap-4">
                                                                            <div className="flex-1 min-w-0">
                                                                                <div className="flex items-center gap-3 mb-3 flex-wrap">
                                                                                    <span className={`px-3 py-1 rounded-lg text-xs font-bold border ${getSeverityColor(finding.severity)}`}>
                                                                                        {finding.severity.toUpperCase()}
                                                                                    </span>
                                                                                    {finding.type && (
                                                                                        <span className="px-2.5 py-1 rounded-md bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 text-xs font-medium">
                                                                                            {getTypeLabel(finding.type)}
                                                                                        </span>
                                                                                    )}
                                                                                </div>
                                                                                <h4 className="font-semibold text-gray-900 dark:text-white mb-2 text-lg">
                                                                                    {finding.description || finding.title || 'Security Finding'}
                                                                                </h4>
                                                                                <div className="flex flex-wrap gap-2 text-sm">
                                                                                    <span className="text-gray-600 dark:text-gray-400 font-medium">{finding.resource}</span>
                                                                                    {finding.port && (
                                                                                        <span className="px-2 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-xs font-mono">
                                                                                            Port: {finding.port}
                                                                                        </span>
                                                                                    )}
                                                                                    {finding.securityGroupId && (
                                                                                        <span className="px-2 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-xs font-mono">
                                                                                            SG: {finding.securityGroupId}
                                                                                        </span>
                                                                                    )}
                                                                                    {finding.username && (
                                                                                        <span className="px-2 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-xs font-mono">
                                                                                            User: {finding.username}
                                                                                        </span>
                                                                                    )}
                                                                                    {finding.bucketName && (
                                                                                        <span className="px-2 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-xs font-mono">
                                                                                            Bucket: {finding.bucketName}
                                                                                        </span>
                                                                                    )}
                                                                                </div>
                                                                            </div>
                                                                            <svg
                                                                                className={`w-5 h-5 text-gray-400 transition-transform flex-shrink-0 ${isExpanded ? 'rotate-180' : ''}`}
                                                                                fill="none"
                                                                                viewBox="0 0 24 24"
                                                                                stroke="currentColor"
                                                                            >
                                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                                                            </svg>
                                                                        </div>
                                                                    </button>
                                                                    {isExpanded && (
                                                                        <div className="px-5 pb-5 border-t border-gray-100 dark:border-gray-700 pt-4 bg-gray-50 dark:bg-gray-900/50">
                                                                            <div className="space-y-4">
                                                                                <div>
                                                                                    <span className="text-sm font-semibold text-gray-900 dark:text-white">Description</span>
                                                                                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1.5 leading-relaxed">{finding.description}</p>
                                                                                </div>
                                                                                {finding.recommendation && (
                                                                                    <div>
                                                                                        <span className="text-sm font-semibold text-gray-900 dark:text-white">Recommendation</span>
                                                                                        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1.5 leading-relaxed">{finding.recommendation}</p>
                                                                                    </div>
                                                                                )}
                                                                                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-3">
                                                                                    {finding.securityGroupName && (
                                                                                        <div>
                                                                                            <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Security Group</span>
                                                                                            <p className="text-sm font-mono text-gray-900 dark:text-white mt-1">{finding.securityGroupName}</p>
                                                                                        </div>
                                                                                    )}
                                                                                    {finding.roleName && (
                                                                                        <div>
                                                                                            <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Role</span>
                                                                                            <p className="text-sm font-mono text-gray-900 dark:text-white mt-1">{finding.roleName}</p>
                                                                                        </div>
                                                                                    )}
                                                                                    {finding.accessKeyId && (
                                                                                        <div>
                                                                                            <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Access Key</span>
                                                                                            <p className="text-sm font-mono text-gray-900 dark:text-white mt-1">{finding.accessKeyId}</p>
                                                                                        </div>
                                                                                    )}
                                                                                    {finding.ageDays !== undefined && (
                                                                                        <div>
                                                                                            <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Age</span>
                                                                                            <p className="text-sm text-gray-900 dark:text-white mt-1">{finding.ageDays} days</p>
                                                                                        </div>
                                                                                    )}
                                                                                    {finding.daysSinceCreation !== undefined && (
                                                                                        <div>
                                                                                            <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Days Since Creation</span>
                                                                                            <p className="text-sm text-gray-900 dark:text-white mt-1">{finding.daysSinceCreation}</p>
                                                                                        </div>
                                                                                    )}
                                                                                    {finding.lastUsed && (
                                                                                        <div>
                                                                                            <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Last Used</span>
                                                                                            <p className="text-sm text-gray-900 dark:text-white mt-1">{new Date(finding.lastUsed).toLocaleDateString()}</p>
                                                                                        </div>
                                                                                    )}
                                                                                    {finding.permission && (
                                                                                        <div>
                                                                                            <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Permission</span>
                                                                                            <p className="text-sm text-gray-900 dark:text-white mt-1">{finding.permission}</p>
                                                                                        </div>
                                                                                    )}
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            );
                                                        })
                                                    )}
                                                </div>
                                            )}

                                            {/* Cost Recommendations */}
                                            {activeTab === 'cost' && (
                                                <div className="space-y-3">
                                                    {getFilteredRecommendations().length === 0 ? (
                                                        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-12 text-center">
                                                            <svg className="w-12 h-12 mx-auto text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                            </svg>
                                                            <p className="text-gray-500 dark:text-gray-400">No recommendations match the selected filters</p>
                                                        </div>
                                                    ) : (
                                                        getFilteredRecommendations().map((rec: any, index: number) => {
                                                            const isExpanded = expandedFinding === `rec-${index}`;
                                                            return (
                                                                <div
                                                                    key={`rec-${index}`}
                                                                    className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden hover:shadow-md transition-shadow"
                                                                >
                                                                    <button
                                                                        onClick={() => setExpandedFinding(isExpanded ? null : `rec-${index}`)}
                                                                        className="w-full p-5 text-left"
                                                                    >
                                                                        <div className="flex items-start justify-between gap-4">
                                                                            <div className="flex-1 min-w-0">
                                                                                <div className="flex items-center gap-3 mb-3 flex-wrap">
                                                                                    {rec.estimatedMonthlySavings && rec.estimatedMonthlySavings > 0 && (
                                                                                        <span className="px-3 py-1 rounded-lg text-xs font-bold bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400 border border-green-200 dark:border-green-800">
                                                                                            💰 Save {formatCurrency(rec.estimatedMonthlySavings)}/mo
                                                                                        </span>
                                                                                    )}
                                                                                    {rec.type && (
                                                                                        <span className="px-2.5 py-1 rounded-md bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 text-xs font-medium">
                                                                                            {getTypeLabel(rec.type)}
                                                                                        </span>
                                                                                    )}
                                                                                    {rec.severity && (
                                                                                        <span className={`px-2.5 py-1 rounded-md text-xs font-semibold border ${getSeverityColor(rec.severity)}`}>
                                                                                            {rec.severity.toUpperCase()}
                                                                                        </span>
                                                                                    )}
                                                                                </div>
                                                                                <h4 className="font-semibold text-gray-900 dark:text-white mb-2 text-lg">
                                                                                    {rec.description || rec.title || 'Cost Recommendation'}
                                                                                </h4>
                                                                                <div className="flex flex-wrap gap-2 text-sm">
                                                                                    <span className="text-gray-600 dark:text-gray-400 font-medium">{rec.resource}</span>
                                                                                    {rec.instanceId && (
                                                                                        <span className="px-2 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-xs font-mono">
                                                                                            Instance: {rec.instanceId}
                                                                                        </span>
                                                                                    )}
                                                                                    {rec.instanceType && (
                                                                                        <span className="px-2 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-xs">
                                                                                            {rec.instanceType}
                                                                                        </span>
                                                                                    )}
                                                                                    {rec.volumeId && (
                                                                                        <span className="px-2 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-xs font-mono">
                                                                                            Volume: {rec.volumeId}
                                                                                        </span>
                                                                                    )}
                                                                                    {rec.bucketName && (
                                                                                        <span className="px-2 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-xs font-mono">
                                                                                            Bucket: {rec.bucketName}
                                                                                        </span>
                                                                                    )}
                                                                                    {rec.dbInstanceId && (
                                                                                        <span className="px-2 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-xs font-mono">
                                                                                            RDS: {rec.dbInstanceId}
                                                                                        </span>
                                                                                    )}
                                                                                </div>
                                                                            </div>
                                                                            <svg
                                                                                className={`w-5 h-5 text-gray-400 transition-transform flex-shrink-0 ${isExpanded ? 'rotate-180' : ''}`}
                                                                                fill="none"
                                                                                viewBox="0 0 24 24"
                                                                                stroke="currentColor"
                                                                            >
                                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                                                            </svg>
                                                                        </div>
                                                                    </button>
                                                                    {isExpanded && (
                                                                        <div className="px-5 pb-5 border-t border-gray-100 dark:border-gray-700 pt-4 bg-gray-50 dark:bg-gray-900/50">
                                                                            <div className="space-y-4">
                                                                                <div>
                                                                                    <span className="text-sm font-semibold text-gray-900 dark:text-white">Description</span>
                                                                                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1.5 leading-relaxed">{rec.description}</p>
                                                                                </div>
                                                                                {rec.recommendation && (
                                                                                    <div>
                                                                                        <span className="text-sm font-semibold text-gray-900 dark:text-white">Recommendation</span>
                                                                                        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1.5 leading-relaxed">{rec.recommendation}</p>
                                                                                    </div>
                                                                                )}
                                                                                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-3">
                                                                                    {rec.averageCpu !== undefined && (
                                                                                        <div>
                                                                                            <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Avg CPU</span>
                                                                                            <p className="text-sm text-gray-900 dark:text-white mt-1">{rec.averageCpu}%</p>
                                                                                        </div>
                                                                                    )}
                                                                                    {rec.size && (
                                                                                        <div>
                                                                                            <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Size</span>
                                                                                            <p className="text-sm text-gray-900 dark:text-white mt-1">{rec.size} GB</p>
                                                                                        </div>
                                                                                    )}
                                                                                    {rec.volumeType && (
                                                                                        <div>
                                                                                            <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Volume Type</span>
                                                                                            <p className="text-sm text-gray-900 dark:text-white mt-1">{rec.volumeType}</p>
                                                                                        </div>
                                                                                    )}
                                                                                    {rec.daysOld !== undefined && (
                                                                                        <div>
                                                                                            <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Age</span>
                                                                                            <p className="text-sm text-gray-900 dark:text-white mt-1">{rec.daysOld} days</p>
                                                                                        </div>
                                                                                    )}
                                                                                    {rec.region && (
                                                                                        <div>
                                                                                            <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Region</span>
                                                                                            <p className="text-sm text-gray-900 dark:text-white mt-1">{rec.region}</p>
                                                                                        </div>
                                                                                    )}
                                                                                    {rec.engine && (
                                                                                        <div>
                                                                                            <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Engine</span>
                                                                                            <p className="text-sm text-gray-900 dark:text-white mt-1">{rec.engine}</p>
                                                                                        </div>
                                                                                    )}
                                                                                    {rec.instanceClass && (
                                                                                        <div>
                                                                                            <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Instance Class</span>
                                                                                            <p className="text-sm text-gray-900 dark:text-white mt-1">{rec.instanceClass}</p>
                                                                                        </div>
                                                                                    )}
                                                                                    {rec.loadBalancerName && (
                                                                                        <div>
                                                                                            <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Load Balancer</span>
                                                                                            <p className="text-sm font-mono text-gray-900 dark:text-white mt-1">{rec.loadBalancerName}</p>
                                                                                        </div>
                                                                                    )}
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            );
                                                        })
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    ) : (
                                        <div className="py-12 text-center text-gray-500">No details available</div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </MainLayout>
    );
}
